(() => {
  const SUBMIT_TIMEOUT_MS = 45000;
  const EMBED_WAIT_MS = 30000;
  const SHOPIFY_FIELD_MAX_LENGTH = 1000;
  // Shopify Forms rejects a single answer over 1,000 characters. These fields
  // are filled in order so a long RFQ keeps every line instead of stopping early.
  const DETAIL_FIELD_IDS = [
    'custom#custom_specifications',
    'custom#rfq_details_2',
    'custom#rfq_details_3',
    'custom#rfq_details_4',
    'custom#rfq_details_5',
    'custom#rfq_details_6',
    'custom#rfq_details_7',
    'custom#rfq_details_8',
    'custom#rfq_details_9',
    'custom#rfq_details_10',
  ];

  function findEmbedForm() {
    const host = document.querySelector('.sg-rq-embed');
    if (!host) return null;

    let form = null;
    const walk = (root) => {
      if (form) return;
      const found = root.querySelector?.('[data-testid="form"]');
      if (found) {
        form = found;
        return;
      }
      root.querySelectorAll?.('*').forEach((el) => {
        if (el.shadowRoot) walk(el.shadowRoot);
      });
    };
    walk(host);
    return form;
  }

  function setEmbedValue(input, value) {
    if (!input) return;
    const proto =
      input.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function fieldLabelFromName(name) {
    return name
      .replace(/^contact\[/, '')
      .replace(/\]$/, '')
      .trim();
  }

  function fieldLimit(input) {
    const max = Number(input?.maxLength);
    if (Number.isFinite(max) && max > 0) return Math.min(max, SHOPIFY_FIELD_MAX_LENGTH);
    return SHOPIFY_FIELD_MAX_LENGTH;
  }

  function packIntoFields(text, fields) {
    const packed = fields.map(() => '');
    let remaining = (text || '').trim();

    fields.forEach((field, index) => {
      if (!remaining) return;
      const header = index === 0 ? '' : 'RFQ continued\n\n';
      const available = field.limit - header.length;
      if (available <= 0) return;

      if (remaining.length <= available) {
        packed[index] = `${header}${remaining}`;
        remaining = '';
        return;
      }

      let cut = remaining.lastIndexOf('\n', available);
      if (cut < Math.floor(available * 0.5)) cut = available;
      packed[index] = `${header}${remaining.slice(0, cut).trimEnd()}`;
      remaining = remaining.slice(cut).replace(/^\n+/, '');
    });

    return { packed, remaining };
  }

  function buildSummary(form) {
    const lines = [];
    const handled = new Set();

    const fields = form.querySelectorAll(
      'input[name^="contact["], textarea[name^="contact["], select[name^="contact["]'
    );

    fields.forEach((field) => {
      if (field.type === 'file' || handled.has(field)) return;
      if (field.type === 'hidden' && field.name !== 'contact[Uploaded file]') return;

      if (field.type === 'checkbox') {
        handled.add(field);
        if (field.checked) lines.push(fieldLabelFromName(field.name));
        return;
      }

      if (field.type === 'radio') {
        if (field.checked) lines.push(`${fieldLabelFromName(field.name)}: ${field.value}`);
        return;
      }

      handled.add(field);
      const value = (field.value || '').trim();
      if (!value) return;
      lines.push(`${fieldLabelFromName(field.name)}: ${value}`);
    });

    const fileName = document.getElementById('QuoteForm-file')?.files?.[0]?.name;
    if (fileName) lines.push(`File: ${fileName}`);

    return lines.join('\n') || 'Quote request (no additional details provided)';
  }

  function extractQuantity(text) {
    const match = (text || '').match(/\d+/);
    return match ? match[0] : '1';
  }

  function showError(message) {
    const errorEl = document.getElementById('sg-rq-error');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    const errorEl = document.getElementById('sg-rq-error');
    if (errorEl) errorEl.hidden = true;
  }

  function setSubmitting(button, submitting) {
    if (!button) return;
    button.disabled = submitting;
    button.textContent = submitting ? 'SUBMITTING...' : 'Submit';
  }

  function getEmbedErrors(embedForm) {
    if (!embedForm) return [];
    return [...embedForm.querySelectorAll('[class*="formError"]')]
      .filter((el) => {
        const text = (el.textContent || '').trim();
        if (!text) return false;
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
      })
      .map((el) => (el.textContent || '').trim())
      .filter(Boolean);
  }

  function isSuccessState() {
    if (document.body.classList.contains('sg-sw-form-success')) return true;
    const embed = document.querySelector('.sg-rq-embed shopify-forms-embed');
    return embed?.shadowRoot?.querySelector('#app-embed')?.getAttribute('data-current-step') === 'success';
  }

  function revealSuccess() {
    const embedHost = document.querySelector('.sg-rq-embed');
    if (embedHost) embedHost.removeAttribute('aria-hidden');
    document.body.classList.add('sg-sw-form-success');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function findEmbedFileInput() {
    return findEmbedForm()?.querySelector('input[type="file"]') || null;
  }

  function watchFileFieldSupport() {
    const fileField = document.querySelector('.sg-rq-file-field');
    if (!fileField) return;

    const startedAt = Date.now();
    const check = window.setInterval(() => {
      if (findEmbedFileInput()) {
        fileField.hidden = false;
        window.clearInterval(check);
        return;
      }
      if (Date.now() - startedAt > EMBED_WAIT_MS) {
        window.clearInterval(check);
      }
    }, 500);
  }

  function initFileControl() {
    const control = document.querySelector('.sg-rq-file');
    const fileInput = document.getElementById('QuoteForm-file');
    if (!control || !fileInput) return;

    const nameEl = control.querySelector('.sg-rq-file__name');
    const removeBtn = control.querySelector('.sg-rq-file__remove');

    const sync = () => {
      const file = fileInput.files?.[0];
      control.dataset.sgFileState = file ? 'uploaded' : 'empty';
      if (nameEl) nameEl.textContent = file ? file.name : '';
    };

    fileInput.addEventListener('change', sync);

    removeBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      fileInput.value = '';
      sync();
      fileInput.focus();
    });

    sync();
  }

  // Clicking anywhere in a date field opens the picker, not just the calendar icon.
  function initDatePickers(form) {
    form.querySelectorAll('input[type="date"]').forEach((input) => {
      if (typeof input.showPicker !== 'function') return;

      input.addEventListener('click', () => {
        try {
          input.showPicker();
        } catch (_) {
          /* Already open, or the browser refused outside a user gesture. */
        }
      });
    });
  }

  function transferFileToEmbed(file) {
    const embedFileInput = findEmbedFileInput();
    if (!embedFileInput) return false;

    const dt = new DataTransfer();
    dt.items.add(file);
    embedFileInput.files = dt.files;
    embedFileInput.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    embedFileInput.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    return true;
  }

  function init() {
    const form = document.getElementById('RequestQuoteForm');
    if (!form || form.dataset.sgBridgeBound === 'true') return;
    form.dataset.sgBridgeBound = 'true';

    const submitBtn = document.getElementById('sg-rq-submit');

    watchFileFieldSupport();
    initFileControl();
    initDatePickers(form);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      clearError();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const embedForm = findEmbedForm();
      if (!embedForm) {
        showError('The form service is still loading. Please wait a moment and try again.');
        return;
      }

      const fileInput = document.getElementById('QuoteForm-file');
      const chosenFile = fileInput?.files?.[0];
      if (chosenFile && !transferFileToEmbed(chosenFile)) {
        showError('File uploads are temporarily unavailable. Please remove the file or email it to salesweb@speedgrip.com.');
        return;
      }

      const byField = (id) =>
        embedForm.querySelector(`[data-testid="field-${id}"]`) || embedForm.querySelector(`[name="${id}"]`);
      const getValue = (id) => (document.getElementById(id)?.value || '').trim();
      const availableFields = (ids) =>
        ids
          .map((id) => {
            const input = byField(id);
            if (!input || input.tagName !== 'TEXTAREA') return null;
            return { id, input, limit: fieldLimit(input) };
          })
          .filter(Boolean);

      setEmbedValue(byField('email'), getValue('QuoteForm-email'));
      setEmbedValue(byField('custom#company'), getValue('QuoteForm-customer') || 'Not provided');
      setEmbedValue(byField('custom#product'), 'Request a Quote');
      setEmbedValue(byField('custom#product_url'), window.location.href);
      setEmbedValue(byField('custom#sales_rep_email'), getValue('QuoteForm-sales-rep-email'));
      setEmbedValue(byField('custom#quantity'), extractQuantity(getValue('QuoteForm-quantities')));

      const detailFields = availableFields(DETAIL_FIELD_IDS);
      const { packed, remaining } = packIntoFields(buildSummary(form), detailFields);
      detailFields.forEach((field, index) => setEmbedValue(field.input, packed[index]));

      if (!detailFields.length || remaining) {
        showError('This request is too long to send in full. Please shorten the longest answers and try again.');
        return;
      }

      const consent = embedForm.querySelector('input[type="checkbox"]');
      if (consent?.checked) consent.click();

      setSubmitting(submitBtn, true);

      const embedSubmit =
        embedForm.querySelector('[data-testid="btn-form-submit"]') ||
        embedForm.querySelector('button[type="submit"]');
      ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach((type) => {
        embedSubmit?.dispatchEvent(new MouseEvent(type, { bubbles: true, composed: true, cancelable: true }));
      });

      const startedAt = Date.now();
      const poll = window.setInterval(() => {
        if (isSuccessState()) {
          window.clearInterval(poll);
          revealSuccess();
          setSubmitting(submitBtn, false);
          return;
        }

        const errors = getEmbedErrors(findEmbedForm());
        if (errors.length) {
          window.clearInterval(poll);
          setSubmitting(submitBtn, false);
          showError(`Could not submit: ${errors[0]}. Please check your entries and try again.`);
          return;
        }

        if (Date.now() - startedAt > SUBMIT_TIMEOUT_MS) {
          window.clearInterval(poll);
          setSubmitting(submitBtn, false);
          showError('Something went wrong sending your request. Please try again or email salesweb@speedgrip.com.');
        }
      }, 400);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
